/**
 * Export-related type definitions
 */

import { GridApi } from "ag-grid-community";
import { BenefitPlanGroup, RfpCoverage } from "@platform/plan-grid-core";

import type { BenefitPlanGroup as BtxRatesBenefitPlanGroup } from "@platform/btx-rates";

export interface UseGridExportParams {
  ecoRfpId: string;
  /** RFP name for the export filename */
  rfpName: string;
  /** Optional callback invoked when an export error occurs */
  onError?: (error: Error) => void;
}

export interface CoverageExportData {
  coverage: RfpCoverage;
  benefitPlanGroups: BenefitPlanGroup[];
}

/**
 * Return value from the export hook
 */
export interface UseGridExportReturn {
  startExport: () => Promise<void>;
  cancelExport: () => void;
  isExporting: boolean;
  coveragesData: CoverageExportData[] | null;
  error: Error | null;
  /** Whether an error has occurred and is being displayed (for UI feedback) */
  hasExportError: boolean;
  /** Manually trigger error state (for errors outside the hook's control) */
  setExportError: (error: Error) => void;
}

export interface HiddenGridRef {
  gridApi: GridApi;
  coverageType: string;
}

export type PlanColumnSpans = Record<string, number>;

export interface ExportPlanLayout {
  planIdsInOrder: string[];
  planColumnSpans: PlanColumnSpans;
}

/**
 * Type of grid being exported
 */
export enum GridType {
  Benefits = "benefits",
  Rates = "rates",
  Summary = "summary",
}

/**
 * Type for cell values in Excel exports
 */
export type CellValue = string | number | null | undefined;

/**
 * Transform PlanDesigner BenefitPlanGroup to btx-rates BenefitPlanGroup format
 *
 * The PlanDesigner API returns BenefitPlanGroup with policyPeriod at the group level,
 * while btx-rates expects it at the plan level. This transform bridges that gap.
 *
 * Key transformations:
 * - Copies policyPeriod from group level down to each plan
 * - Converts enums to strings (TypeScript does this automatically)
 * - Adds minimal plan fields required by btx-rates components
 *
 * @param groups - Benefit plan groups from PlanDesigner API
 * @returns Benefit plan groups compatible with btx-rates package
 */
export const transformToBtxRatesFormat = (
  groups: BenefitPlanGroup[],
): BtxRatesBenefitPlanGroup[] =>
  groups.map((group) => ({
    id: group.id,
    productType: group.productType as string,
    policyPeriod: group.policyPeriod as string,
    position: group.position,
    nickname: group.nickname,
    size: group.size,
    benefitPlans: group.benefitPlans.map((plan) => ({
      id: plan.id,
      identifier: plan.identifier,
      policyPeriod: group.policyPeriod as string, // Copy from group to plan
      optionName: plan.optionName as string,
      type: "", // Not used by export components
    })),
  }));
